import React from 'react'
import { Formik, FormikProps } from 'formik'
import * as yup from 'yup'

import Form from 'components/Form'
import UnTypedInput from 'components/Input'
import Button from 'components/Button'
import {
  useUserRequestPasswordResetMutation,
  UserGeneratePersonalTokenMutationResult,
  UserRequestPasswordResetMutation,
  UserRequestPasswordResetMutationVariables,
} from 'gqlTypes'
import { MutationOptionsWithoutRedirectedErrors } from 'graphql/apollo/useMutation/useMutation.types'

const Input = UnTypedInput as any

const forgotPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .email('This is not a valid email')
    .required('Email can’t be blank'),
})

interface Values {
  email: string
}

type UserErrors = NonNullable<
  NonNullable<
    UserGeneratePersonalTokenMutationResult['data']
  >['userGeneratePersonalToken']
>['errors']

export interface ExtendedFormikProps extends FormikProps<Values> {
  successful: boolean
  apiErrors: UserErrors
}

export const Fields: React.FC<ExtendedFormikProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
}) => (
  <Form.Field
    name="email"
    label="Email"
    errorText={touched.email ? errors.email : undefined}>
    <Input
      name="email"
      type="email"
      placeholder="john@appleseed.com"
      value={values.email}
      onChange={handleChange}
      onBlur={handleBlur}
      autoFocus
    />
  </Form.Field>
)

export const Submit: React.FC<ExtendedFormikProps> = ({ isSubmitting }) => (
  <Form.Footer>
    <Button
      variant="primary-untinted"
      type="submit"
      fill
      small
      disabled={isSubmitting}
      loading={isSubmitting}>
      Reset Password
    </Button>
  </Form.Footer>
)

type MutationParameters = MutationOptionsWithoutRedirectedErrors<
  UserRequestPasswordResetMutation,
  UserRequestPasswordResetMutationVariables
>
type MutationHandlers = Pick<MutationParameters, 'onCompleted' | 'onError'>

const defaultFormFields = (formProps: ExtendedFormikProps) => (
  <>
    <Fields {...formProps} />
    <Submit {...formProps} />
  </>
)

interface ForgotPasswordFormProps extends MutationHandlers {
  initialEmail?: string
  children?: (formikProps: ExtendedFormikProps) => React.ReactNode
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  children = defaultFormFields,
  initialEmail,
  onError,
  onCompleted,
}) => {
  const [
    resetPassword,
    { loading, data },
  ] = useUserRequestPasswordResetMutation({
    redirectErrors: false,
    onCompleted,
    onError,
  })

  const apiErrors = data?.userRequestPasswordReset?.errors || []
  const successful = !!data?.userRequestPasswordReset?.successful

  return (
    <Formik
      initialValues={{ email: initialEmail || '' }}
      onSubmit={async ({ email }) => {
        try {
          await resetPassword({ variables: { email } })
        } catch (e) {
          // The error will be handled by the mutation "onError"
        }
      }}
      validationSchema={forgotPasswordSchema}>
      {formikBag => (
        <Form>
          {children({
            ...formikBag,
            isSubmitting: loading,
            successful,
            apiErrors,
          })}
        </Form>
      )}
    </Formik>
  )
}

export default ForgotPasswordForm
